import {
  type InsuranceProduct,
  InsuranceType
} from '@coolblue-development/next-insurance/insurance-selector';
import type { Accessory, Product } from '@features/add-to-cart-page/typings/Product';

import { type SummaryItem, SummaryItemPaymentType, SummaryItemType } from './SummaryStore.types';

export function mapProductToSummaryItem(product: Product, quantity?: number): SummaryItem {
  return {
    id: product.id,
    name: product.name,
    brand: product.brand,
    ...(product.listPrice && {
      listPrice: {
        includingVat: product.listPrice.includingVat,
        excludingVat: product.listPrice.excludingVat
      }
    }),
    salesPrice: {
      includingVat: product.salesPrice.includingVat,
      excludingVat: product.salesPrice.excludingVat ?? 0
    },

    paymentType: SummaryItemPaymentType.OneTime,
    type: SummaryItemType.Accessory,
    quantity: quantity || 1,
    showPrice: true,
    personalPrice: mapPersonalPrice(product.personalPrice)
  };
}

export function mapInsuranceProductToSummaryItem(insuranceProduct: InsuranceProduct): SummaryItem {
  return {
    id: insuranceProduct.quoteId ?? insuranceProduct.id,
    name: insuranceProduct.title,
    salesPrice: {
      includingVat: insuranceProduct.premium
        ? insuranceProduct.premium.includingVat
        : insuranceProduct.salesPrice.includingVat
    },
    paymentType:
      insuranceProduct.paymentType === 'monthly'
        ? SummaryItemPaymentType.Monthly
        : SummaryItemPaymentType.OneTime,
    type:
      insuranceProduct.insuranceType === InsuranceType.EXTENDED_WARRANTY
        ? SummaryItemType.ExtendedWarranty
        : SummaryItemType.Insurance,
    quantity: 1,
    showPrice: true
  };
}

export function mapAccessoryProductToSummaryItem(
  accessoryProduct: Accessory,
  quantity?: number
): SummaryItem {
  return {
    id: accessoryProduct.product.id,
    name: accessoryProduct.product.name,
    ...(accessoryProduct.product.prices.listPrice && {
      listPrice: {
        includingVat: accessoryProduct.product.prices.listPrice.includingVat,
        excludingVat: accessoryProduct.product.prices.listPrice.excludingVat
      }
    }),
    salesPrice: {
      includingVat: accessoryProduct.product.prices.salesPrice.includingVat,
      excludingVat: accessoryProduct.product.prices.salesPrice.excludingVat ?? 0
    },
    paymentType: SummaryItemPaymentType.OneTime,
    type: SummaryItemType.Accessory,
    quantity: quantity || 1,
    showPrice: true,
    personalPrice: mapPersonalPrice(
      accessoryProduct.product.personalPrice ?? undefined,
      quantity || 1
    ),
    shortName: accessoryProduct.product.shortName
  };
}

export function mapPersonalPrice(
  personalPrice: Product['personalPrice'],
  quantity: number = 1
): SummaryItem['personalPrice'] | undefined {
  if (!personalPrice) {
    return undefined;
  }

  return {
    price: {
      includingVat: personalPrice.price.includingVat * quantity,
      excludingVat: personalPrice.price.excludingVat
        ? personalPrice.price.excludingVat * quantity
        : undefined
    },
    labelText: personalPrice.labelText
  };
}
